import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { usePageTitle } from "../hooks/usePageTitle";

const Register = () => {
  usePageTitle("Sign Up");
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    // Check password complexity
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).*$/;
    if (!passwordRegex.test(formData.password)) {
      setError(
        "Password must contain at least one uppercase letter, one lowercase letter, and one number",
      );
      return;
    }

    // Check terms agreement
    if (!agreedToTerms) {
      setError("You must agree to the Terms of Service and Privacy Policy");
      return;
    }

    setLoading(true);

    const result = await register({
      username: formData.username,
      email: formData.email,
      password: formData.password,
    });

    if (result.success) {
      setSuccess(true);
      setError("");
      setFormData({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
      });

      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } else {
      setError(result.message);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md space-y-4">
        {/* MVP Warning Banner */}
        <div className="bg-yellow-500/10 border border-yellow-500/50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div>
              <h3 className="text-yellow-500 font-semibold text-sm mb-1">
                Early Access Notice
              </h3>
              <p className="text-yellow-200/90 text-xs leading-relaxed">
                Password recovery is <strong>not yet available</strong>. Please
                save your password securely! If you lose it, contact{" "}
                <a
                  href="mailto:privacy@collectarena.com"
                  className="underline hover:text-yellow-100"
                >
                  privacy@collectarena.com
                </a>{" "}
                for manual reset. Automatic recovery coming soon as we grow!
              </p>
            </div>
          </div>
        </div>

        {/* Registration Form */}
        <div className="bg-dark-card p-8 rounded-lg shadow-xl border border-gray-700">
          <h2 className="text-3xl font-bold text-white mb-6 text-center">
            Sign Up
          </h2>

          {error && (
            <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-500/10 border border-green-500 text-green-500 px-4 py-3 rounded mb-4">
              <p className="font-semibold">Registration successful!</p>
              <p className="text-sm mt-1">Redirecting to login page...</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-300 mb-2">Username</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                minLength="3"
                disabled={success}
                className="w-full px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 transition disabled:opacity-50"
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={success}
                className="w-full px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 transition disabled:opacity-50"
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength="8"
                disabled={success}
                className="w-full px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 transition disabled:opacity-50"
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2">
                Confirm Password
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                disabled={success}
                className="w-full px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 transition disabled:opacity-50"
              />
            </div>

            {/* GDPR CONSENT CHECKBOX */}
            <div className="pt-2">
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  required
                  disabled={success}
                  className="mt-1 mr-3 h-4 w-4 rounded border-gray-600 bg-dark-bg text-blue-600 focus:ring-blue-500 focus:ring-offset-dark-bg cursor-pointer disabled:opacity-50"
                />
                <span className="text-sm text-gray-300 leading-relaxed">
                  I agree to the{" "}
                  <Link
                    to="/terms"
                    className="text-blue-400 hover:text-blue-300 underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link
                    to="/privacy"
                    className="text-blue-400 hover:text-blue-300 underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </Link>
                  <span className="text-red-400 ml-1">*</span>
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={!agreedToTerms || loading || success}
              className={`w-full font-semibold py-2 px-4 rounded-lg transition ${
                !agreedToTerms || loading || success
                  ? "bg-gray-600 cursor-not-allowed opacity-50"
                  : "bg-blue-600 hover:bg-blue-700"
              } text-white`}
            >
              {loading
                ? "Creating account..."
                : success
                  ? "Redirecting..."
                  : "Sign Up"}
            </button>
          </form>

          <p className="text-gray-400 text-center mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-blue-400 hover:text-blue-300">
              Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
